import { ReactEditor, RenderElementProps } from 'slate-react';
import {
  DocumentTranscriptParagraphV1,
  ScriptixDescendant
} from '@utils/types/editors/transcripts/transcript.ts';
import { useEffect, useRef } from 'react';
import { useAudio } from '@utils/providers/audio-context.tsx';
import { changeSpeaker } from '@utils/editors/change-speaker.ts';
import { currentParagraphIsSeeking } from '@utils/editors/current-paragraph-is-seeking.ts';
import { useSharedTranscript } from '@utils/providers/shared-transcript-context.tsx';
import { SharedTranscriptSpeaker } from '@components/editors/shared-editor/shared-transcript/shared-transcript-speaker.tsx';
import { useSharedEditorContext } from '@utils/providers/shared-editor-context.tsx';
import { useThemeContext } from '@utils/providers/theme-context.tsx';

export const SharedParagraphElement = ({
  attributes,
  children,
  element,
  onWordOutOfView,
  onWordInView,
  editor,
  index
}: RenderElementProps & {
  onWordOutOfView: (
    ref: HTMLSpanElement | null,
    direction: 'up' | 'down'
  ) => void;
  onWordInView: () => void;
  editor: ReactEditor;
  index: number;
}) => {
  const { currentTime, linkedPlayback } = useAudio();
  const { addSpeaker, replaceSpeakers } = useSharedTranscript();
  const paragraphRef = useRef<HTMLDivElement | null>(null);
  const { replaceSpeakersInEditor } = useSharedEditorContext();
  const { theme } = useThemeContext();

  // Theme-aware colors
  const pick = (light: string, dark: string) =>
    theme === 'dark' ? dark : light;

  const shadowColor = pick('rgba(209,213,219,0.4)', 'rgba(55,65,81,0.4)'); // gray-300/gray-700

  const isCurrentWord = currentParagraphIsSeeking(
    element as DocumentTranscriptParagraphV1,
    currentTime
  );
  const { type, id, speaker, start } = element as ScriptixDescendant;
  useEffect(() => {
    if (!linkedPlayback) return;

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (isCurrentWord) {
            const direction = entry.boundingClientRect.top < 0 ? 'up' : 'down';

            if (!entry.isIntersecting && entry.intersectionRatio === 0) {
              onWordOutOfView(paragraphRef.current, direction);
            } else if (entry.isIntersecting && entry.intersectionRatio > 0) {
              onWordInView();
            }
          }
        });
      },
      { root: null, threshold: [0, 0.1, 0.5, 1] }
    );

    if (paragraphRef.current) {
      observer.observe(paragraphRef.current);
    }
    return () => {
      if (paragraphRef.current) {
        observer.unobserve(paragraphRef.current);
      }
    };
  }, [isCurrentWord, onWordOutOfView, onWordInView, linkedPlayback]);

  const handleSetSpeaker = (value: string) => {
    addSpeaker(value, element as ScriptixDescendant);
    changeSpeaker(value, element as ScriptixDescendant, editor);
  };

  const handleReplaceSpeakers = (value: string) => {
    if (!value || !speaker) return;

    // Update transcript in state
    replaceSpeakers(speaker, value);

    // Update UI
    replaceSpeakersInEditor(speaker, value);
  };

  return (
    <div ref={paragraphRef}>
      {type === 'speaker' ? (
        <div
          className="flex items-start w-full pr-2 mb-4 overflow-x-auto"
          key={id}
          {...attributes}
          contentEditable={false}
          style={{ boxShadow: `0 1px 0 0 ${shadowColor}` }}
        >
          <SharedTranscriptSpeaker
            speaker={speaker || ''}
            start={start || 0}
            setSpeaker={handleSetSpeaker}
            index={index}
            replaceSpeakers={handleReplaceSpeakers}
          />
        </div>
      ) : (
        <div
          {...attributes}
          onMouseDown={() => {
            // Tell everyone which row is active (only this index should be active)
            window.dispatchEvent(
              new CustomEvent('transcript:activeRow', { detail: { index } })
            );
          }}
        >
          {children}
        </div>
      )}
    </div>
  );
};
